import React, { useRef, useState, useLayoutEffect, useMemo } from 'react'
import { data } from '../data/content'
import { useLocation, useParams } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import AnimatedPage from '../components/AnimatedPage'
import { calcInteractions } from '../helpers/functions'
import { gsap } from 'gsap'
import bgImageHome from '../assets/Start/Start_Still.jpg'
import bgClean from '../assets/General/General_Clean.jpg'

const fadeIn = {
  initial: {
    opacity: 0,
  },
  animate: {
    opacity: 1,
    transition: {
      duration: 1,
      // delay: 0.5,
    },
  },
  exit: {
    opacity: 0,
  },
}

const ChildPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const thisRef = useRef()
  const timeoutRef = useRef()
  const [interactionsWidth, setInteractionsWidth] = useState('0px')
  const [interactionsHeight, setInteractionsHeight] = useState('0px')
  const [introIsFinished, setIntroIsFinished] = useState(false)
  const [showActionButton, setShowActionButton] = useState(false)
  const [showActionNavButton, setShowActionNavButton] = useState(false)

  const { category, index } = useParams()
  const { categories } = data
  const { categorySlug, segments } = categories.filter(
    (cat) => cat.categorySlug === category
  )[0]

  const shouldFadeIn = [0, 1].includes(location?.state?.transition)
  const previousBgStill = location?.state?.bgStill
  const isPlayAll = location?.state?.isPlayAll

  const { actionButtonStyles } = segments[index]

  const bgImage = shouldFadeIn ? bgClean : previousBgStill

  const initialBgImage =
    location?.state?.transition === 0 ? bgImageHome : bgImage

  const videoTransitionIn =
    require(`../assets/${categorySlug}/${index}_Intro.mp4`).default
  const bgStill =
    require(`../assets/${categorySlug}/${index}_Still.jpg`).default

  const currentCatIndex = categories.findIndex(
    (cat) => cat.categorySlug === category
  )

  const isLastSegment = parseInt(index) === segments.length - 1
  const isLastCat = currentCatIndex === categories.length - 1

  const nextSlide = useMemo(() => {
    if (isLastCat && isLastSegment) {
      return '/outro'
    } else if (isLastSegment) {
      return isPlayAll
        ? `/${categories[currentCatIndex + 1].categorySlug}/0`
        : '/outro'
    } else {
      return `/${categorySlug}/${parseInt(index) + 1}`
    }
  }, [
    categories,
    categorySlug,
    currentCatIndex,
    index,
    isLastCat,
    isLastSegment,
    isPlayAll,
  ])

  const prevSlide = useMemo(() => {
    const currentIndex = parseInt(index)

    if (currentCatIndex === 0 && currentIndex === 0) {
      return '/'
    } else if (currentIndex === 0) {
      return isPlayAll
        ? `/${categories[currentCatIndex - 1].categorySlug}/${
            categories[currentCatIndex - 1].segments.length - 1
          }`
        : '/'
    } else {
      return `/${categorySlug}/${parseInt(index) - 1}`
    }
  }, [categories, categorySlug, currentCatIndex, index, isPlayAll])

  const handleBgClick = () => {
    if (introIsFinished && isLastSegment && !showActionNavButton) {
      setShowActionNavButton(true)
    }
  }

  useLayoutEffect(() => {
    let isMounted = true
    const sixteenNineAspect = 16 / 9
    const viewportHeight = window.innerHeight
    const viewportWidth = window.innerWidth
    const widthIfConstarainedByHeight = viewportHeight * sixteenNineAspect
    const heightIfConstarainedByWidth = viewportWidth / sixteenNineAspect
    const videoTransitionInElement = thisRef.current.querySelector(
      '.video-transition-in'
    )

    if (widthIfConstarainedByHeight <= viewportWidth) {
      setInteractionsWidth(widthIfConstarainedByHeight + 'px')
      setInteractionsHeight(viewportHeight + 'px')
    } else {
      setInteractionsWidth(viewportWidth + 'px')
      setInteractionsHeight(heightIfConstarainedByWidth + 'px')
    }

    window.addEventListener('resize', calcInteractions)

    if (bgStill !== '') {
      const bgMediaElement = thisRef.current.querySelector('.bg-media')

      const displayStill = () => {
        setIntroIsFinished(true)
        setShowActionButton(true)
        bgMediaElement.classList.remove('d-none')
      }

      videoTransitionInElement.addEventListener('ended', () => {
        displayStill()

        if (isLastSegment && !showActionNavButton) {
          setTimeout(() => {
            setShowActionNavButton(true)
          }, 10000)
        }
      })
    }

    const playTransitionIn = async () => {
      try {
        await videoTransitionInElement.play()
      } catch (error) {
        console.log(error)
      }
    }

    // transition in
    const videoTransitionIn = setTimeout(() => {
      if (isMounted) {
        playTransitionIn()
      }
    }, 0)

    // navigation
    const ctx = gsap.context(() => {
      const bg2 = thisRef.current.querySelector('.bg-2')
      const footnote = thisRef.current.querySelector('.footnote')
      const tween = gsap.to(bg2, {
        opacity: 1,
        duration: 0.5,
        paused: true,
      })
      const tween2 = gsap.to(footnote, {
        opacity: 0,
        duration: 0.5,
        paused: true,
      })

      const navTo = (e) => {
        e.stopPropagation()
        const link = e.target
        const navToRoute = link.dataset.route
        const shouldFade = link.dataset.fadetransition === 'true'

        if (shouldFade) {
          tween.play(0, false)
          tween2.play(0, false)
          timeoutRef.current = setTimeout(() => {
            navigate(navToRoute, {
              state: {
                transition: 1,
                from: location.pathname,
                isPlayAll,
              },
            })
          }, 500)
        } else {
          navigate(navToRoute, {
            state: {
              bgStill,
              isPlayAll,
            },
          })
        }
      }

      const navBtns = thisRef.current.querySelectorAll('.nav-btn, .action-btn')

      navBtns.forEach((navBtn) => {
        navBtn.addEventListener('click', navTo)
      })

      // Cleanup event listeners
      return () => {
        navBtns.forEach((navBtn) => {
          navBtn.removeEventListener('click', navTo)
        })
      }
    })

    // Unmount -> cleanup
    return () => {
      isMounted = false
      ctx.revert()
      clearTimeout(timeoutRef.current)
      clearTimeout(videoTransitionIn)
      window.removeEventListener('resize', calcInteractions)
    }
  }, [
    navigate,
    bgStill,
    thisRef,
    actionButtonStyles,
    isLastSegment,
    index,
    isPlayAll,
    location.pathname,
    showActionNavButton,
  ])

  return (
    <AnimatedPage>
      <main
        className='d-flex flex-grow-1 flex-column justify-content-center align-items-center bg-black overflow-hidden'
        ref={thisRef}
      >
        <div className='ratio ratio-16x9 mh-100vh'>
          {/* Displays before Intro Video loads */}
          <img
            src={initialBgImage}
            style={{ objectFit: 'contain' }}
            alt='background'
          />
          {/* Slide Intro Video */}
          <motion.video
            variants={shouldFadeIn && fadeIn}
            initial='initial'
            animate='animate'
            exit='exit'
            src={videoTransitionIn}
            type='video/mp4'
            className='video-transition-in w-100'
            muted
          />
          {/* Slide Still Image */}
          <img
            src={bgStill}
            className='bg-media d-none w-100'
            style={{ objectFit: 'contain' }}
            alt='background'
          />
          {/* Background to fade to if shouldFade is true */}
          <img
            className='bg-2'
            src={bgClean}
            style={{ objectFit: 'contain', opacity: 0 }}
            alt='background'
          />
          <div
            className='interactions overflow-hidden'
            style={{ width: interactionsWidth, height: interactionsHeight }}
            onClick={handleBgClick}
          >
            <motion.p
              variants={shouldFadeIn && fadeIn}
              initial='initial'
              animate='animate'
              exit='exit'
              className='footnote text-white fw-light mb-0'
              style={{
                position: 'absolute',
                fontSize: '0.65vw',
                width: 'fit-content',
                height: 'fit-content',
                top: '92.95%',
                left: '4%',
              }}
            >
              Microsoft Copilot is a trademarked product of Microsoft
              Corporation. The features and functionality displayed in this demo
              are subject to change at any time.
            </motion.p>
            <div
              data-route={prevSlide}
              data-fadetransition={parseInt(index) === 0}
              className='position-absolute nav-btn nav-btn-prev'
            />

            <div
              data-route='/'
              data-fadetransition={true}
              className='position-absolute nav-btn nav-btn-home'
            />

            <div
              data-route={nextSlide}
              data-fadetransition={isLastSegment}
              className={`position-absolute nav-btn nav-btn-next${
                showActionNavButton ? ' action-nav-btn' : ''
              }`}
            />
            {actionButtonStyles && (
              <div
                data-route={nextSlide}
                data-fadetransition={isLastSegment}
                className='position-absolute action-btn'
                style={{
                  opacity: showActionButton ? 1 : 0,
                  ...actionButtonStyles,
                }}
              />
            )}
          </div>
        </div>
      </main>
    </AnimatedPage>
  )
}

export default ChildPage
